/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0
 * RFC 7636: https://datatracker.ietf.org/doc/html/rfc7636
 *
 * Used to secure the authorization-code flow by binding the authorization
 * request to the token request via a hashed verifier. In Exchange's
 * popup-relogin flow (W-22351940) the verifier is generated here in the
 * browser and travels with the authorization code to exchange-ui's own
 * server, which forwards both to cs over mTLS. The verifier never leaves
 * the user's tab + our trusted server boundary, so it still binds the
 * /authorize and /token requests as PKCE intends.
 */

/**
 * Generate a cryptographically random code verifier per RFC 7636 §4.1
 * (high-entropy string drawn from the unreserved character set, length
 * between 43 and 128 characters). 32 random bytes base64url-encoded gives
 * us 43 characters.
 *
 * @returns {string} the code verifier
 */
export function generateCodeVerifier() {
  const randomBytes = new Uint8Array(32);

  if (typeof window !== 'undefined' && window.crypto) {
    window.crypto.getRandomValues(randomBytes);
  } else {
    // Node fallback for unit tests
    // eslint-disable-next-line global-require
    const nodeCrypto = require('crypto');

    if (!nodeCrypto || typeof nodeCrypto.randomFillSync !== 'function') {
      // Never return predictable (all-zero) bytes as a verifier — that would
      // silently defeat PKCE. Fail loudly instead.
      throw new Error('PKCE: no secure random source available');
    }

    nodeCrypto.randomFillSync(randomBytes);
  }

  return base64URLEncode(randomBytes);
}

/**
 * Generate the SHA-256 code challenge from the verifier per RFC 7636 §4.2.
 * code_challenge = BASE64URL(SHA256(ASCII(code_verifier)))
 *
 * @param {string} codeVerifier
 * @returns {Promise<string>} the code challenge
 */
export async function generateCodeChallenge(codeVerifier) {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);

    return base64URLEncode(new Uint8Array(digest));
  }

  // Node fallback for unit tests
  // eslint-disable-next-line global-require
  const nodeCrypto = require('crypto');
  const hash = nodeCrypto.createHash('sha256').update(codeVerifier).digest();

  return base64URLEncode(hash);
}

/**
 * Convenience helper that returns both the verifier and challenge in one
 * call.
 *
 * @returns {Promise<{codeVerifier: string, codeChallenge: string}>}
 */
export async function generatePKCEPair() {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  return { codeVerifier, codeChallenge };
}

/**
 * Base64-URL encode without padding per RFC 7636 Appendix A.
 */
function base64URLEncode(buffer) {
  let base64;

  if (typeof window !== 'undefined' && window.btoa) {
    const bytes = new Uint8Array(buffer);
    const binaryString = String.fromCharCode(...bytes);

    base64 = window.btoa(binaryString);
  } else {
    base64 = Buffer.from(buffer).toString('base64');
  }

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
